import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IntroTitle, TypingCaret, TypingText } from './AuthLayout.styled';

const LANGS = ['pl', 'en', 'de'] as const;
type Lang = typeof LANGS[number];

const TYPE_MS = 55;
const DELETE_MS = 25;
const HOLD_MS = 2400;
const GAP_MS = 450;

const isLang = (value: string | undefined): value is Lang => LANGS.includes(value as Lang);

// Język startowy: ten z linku (/pl/..., /en/..., /de/...), potem bieżący język i18n.
const getStartLang = (fallback: string | undefined): Lang => {
  const fromPath = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1];
  if (isLang(fromPath)) return fromPath;
  const fromI18n = fallback?.slice(0, 2);
  return isLang(fromI18n) ? fromI18n : 'pl';
};

// Kolejny język losowo, ale nigdy ten sam dwa razy z rzędu.
const pickNextLang = (current: Lang): Lang => {
  const others = LANGS.filter((lang) => lang !== current);
  return others[Math.floor(Math.random() * others.length)];
};

const prefersReducedMotion = (): boolean => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

/* Powitanie pisane na żywo jak w hero landingu: tekst w pl/en/de na zmianę,
   zawsze zaczyna od języka panelu z linku, dalej losowo. */
export const TypingIntro: FC = () => {
  const { i18n } = useTranslation();
  const [lang, setLang] = useState<Lang>(() => getStartLang(i18n.language));
  const [length, setLength] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const isReduced = useMemo(prefersReducedMotion, []);

  const text = useMemo(() => {
    const fixedT = i18n.getFixedT(lang);
    return `${fixedT('auth.signIn.typingLine1')}\n${fixedT('auth.signIn.typingLine2')}`;
  }, [i18n, lang]);

  useEffect(() => {
    if (isReduced) return undefined;

    let delay: number;
    let step: () => void;

    if (!isDeleting) {
      if (length < text.length) {
        delay = TYPE_MS;
        step = () => setLength((value) => value + 1);
      } else {
        delay = HOLD_MS;
        step = () => setIsDeleting(true);
      }
    } else if (length > 0) {
      delay = DELETE_MS;
      step = () => setLength((value) => value - 1);
    } else {
      delay = GAP_MS;
      step = () => {
        setLang((current) => pickNextLang(current));
        setIsDeleting(false);
      };
    }

    const timer = window.setTimeout(step, delay);
    return () => window.clearTimeout(timer);
  }, [isDeleting, length, text, isReduced]);

  const visible = isReduced ? text : text.slice(0, length);

  return (
    <IntroTitle id="auth-intro-title" lang={lang} aria-label={text.replace('\n', ' ')}>
      <TypingText aria-hidden="true">
        {visible}
        <TypingCaret />
      </TypingText>
    </IntroTitle>
  );
};
